import { Primitives } from '@client/style/primitive.constants';

/**
 * Chrome's print pipeline (`page.pdf()`, Cmd+P) composites HTML/CSS/images but
 * typically does **not** rasterize a WebGL canvas — even with
 * `preserveDrawingBuffer` and `printBackground`. The cover 3D map is Mapbox +
 * three.js, so a live canvas that looks correct on screen still exports as a
 * blank slot. Snapshot to a PNG `<img>` after the map is idle, then print.
 */

/**
 * Snapshot a canvas to a PNG data URL for print/PDF capture.
 * Returns `undefined` when the canvas is empty, tainted, or not a PNG data URL.
 */
export const snapshotCanvasToDataUrl = (
  canvas: HTMLCanvasElement,
): string | undefined => {
  if (canvas.width === 0 || canvas.height === 0) return undefined;
  try {
    const dataUrl = canvas.toDataURL('image/png');
    if (!dataUrl.startsWith('data:image')) return undefined;
    return dataUrl;
  } catch {
    // Tainted canvas (cross-origin pixels) throws; leave the canvas in place.
    return undefined;
  }
};

/**
 * Matches report page `theme.palette.warmSurface.main`. Readonly Map3d uses a
 * transparent style so the live cover shows the building on this fill.
 */
export const COVER_MAP_SNAPSHOT_BACKGROUND =
  Primitives.colors.warm_surface.main;

/**
 * Snapshot a (possibly transparent) canvas composited onto an opaque fill.
 * JPEG/WebGL `toDataURL` turns alpha into black; the cover map has no basemap
 * tiles, so the live view is the building on the page background.
 */
export const snapshotCanvasOntoBackground = (
  canvas: HTMLCanvasElement,
  background: string,
): string | undefined => {
  if (canvas.width === 0 || canvas.height === 0) return undefined;
  const dest = document.createElement('canvas');
  dest.width = canvas.width;
  dest.height = canvas.height;
  const ctx = dest.getContext('2d');
  if (!ctx) return undefined;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, dest.width, dest.height);
  try {
    ctx.drawImage(canvas, 0, 0);
    const dataUrl = dest.toDataURL('image/png');
    if (!dataUrl.startsWith('data:image')) return undefined;
    return dataUrl;
  } catch {
    return undefined;
  }
};

const copyCanvasLayoutToImage = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
): void => {
  img.className = canvas.className;
  img.style.cssText = canvas.style.cssText;
  const { width, height } = window.getComputedStyle(canvas);
  img.style.width = width;
  img.style.height = height;
  img.style.display = 'block';
};

/**
 * Replaces each `canvas` under `root` with a PNG `<img>` of the same layout.
 * Used immediately before PDF capture so Chromium prints a regular image
 * instead of a WebGL buffer. Canvases that cannot be read are left untouched.
 *
 * @returns The inserted images (so the caller can wait for decode).
 */
export const replaceCanvasesWithImages = (
  root: ParentNode,
): HTMLImageElement[] => {
  const images: HTMLImageElement[] = [];
  for (const canvas of [...root.querySelectorAll('canvas')]) {
    const dataUrl = snapshotCanvasToDataUrl(canvas);
    if (!dataUrl) continue;
    const img = document.createElement('img');
    img.alt = '';
    img.src = dataUrl;
    copyCanvasLayoutToImage(canvas, img);
    canvas.replaceWith(img);
    images.push(img);
  }
  return images;
};

/**
 * Resolves when the image has decoded (or failed). Data-URL snapshots are
 * inlined (no network); treat them as ready so PDF capture does not wait on
 * a `load` event that jsdom/some engines never fire for `data:`.
 */
export const waitForImageLoad = (img: HTMLImageElement): Promise<void> => {
  if (img.complete || img.src.startsWith('data:')) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });
};
